import React, { PropsWithChildren, ReactNode } from "react";
import { Flex, Text, HStack, Heading, StackProps, VStack } from "@chakra-ui/react";
import TaskTag from "../../TaskTag";
import { MultiTaskTitle, Task, TaskType } from "data-models/task-models";
import { useFollowUp } from "providers/FollowUpProvider";
import { useTranslation } from "react-i18next";
import HelpdeskComponent from "common/Helpdesk";

export function TaskHeadingDescription(props: PropsWithChildren<{}>) {
  return (
    <Flex>
      <TaskTag>{props.children}</TaskTag>
    </Flex>
  );
}
export function TaskHeadingTitle(props: PropsWithChildren<{}>) {
  return (
    <Heading w="full" textAlign="start" as="h1" fontSize="2xl" fontWeight="semibold" mt="3" lineHeight="1">
      {props.children}
    </Heading>
  );
}
export function TaskHeadingSubtitle(props: PropsWithChildren<{}>) {
  return (
    <Text fontWeight="semibold" mt="3" alignSelf="baseline" pb="2">
      {props.children}
    </Text>
  );
}

const subtitleTasks = [
  "f7c5e6d2-3fcb-4f0c-875d-9e8a27b5ec5b",
  "b7e94c47-17c6-4e44-934b-4c0f71807e3a",
  "9f3c5b96-3e56-4ab1-9e37-96307a221ed9",
  "fc6b3a64-3dcb-4a98-b26e-bf3e67dce1a4",
  "d9a35239-f060-456d-9aef-88cf422c010b",
  "3f7d9b2c-1a64-4e8f-9c3b-6d8f2a1c7e45",
  "9e3a7b4f-2c6d-4f8a-a9e1-6b2d4c7f3e91",
  "8f3a6b74-2c91-4e7f-9a1d-6e5b0c2f9d43"
];

interface TaskHeadingProps extends StackProps {
  description: string;
  headingTitle?: ReactNode;
  subtitle?: ReactNode;
}
export const TaskHeading = ({ ...rest }: TaskHeadingProps) => {
  const { currentTaskIndex, currentTask } = useFollowUp();
  const { step, title, id, criteria, name, subtitle } = currentTask;
  const { i18n, t } = useTranslation();

  return (
    <HStack w="full" align="center" {...rest}>
      <Flex justify="center" align="center" h="full">
        <Text px="3" py="3" w="fit-content" fontSize="20px" color="white" bg="#2A0F74" borderRadius="100">
          0{currentTaskIndex + 1}
        </Text>
      </Flex>
      <VStack w="100%">
        <HStack w="100%" align="center" justifyContent="space-between">
          <Text textAlign="start" w="full">
            {name[0] === "Kretslopp" || name[0] === "Fysik" || name[0] === "Biologi" || name[0] === "Teknik"
              ? i18n.language === "en"
                ? name[1]
                : i18n.language === "de"
                  ? name[2]
                  : name[0]
              : i18n.language === "en"
                ? criteria[1]
                : i18n.language === "de"
                  ? criteria[2]
                  : criteria[0]}
          </Text>
          <VStack align="end" w="100%">
            <HelpdeskComponent />
          </VStack>
        </HStack>
        <TaskHeadingTitle key={`${id}_${title}_${step}`}>
          {i18n.language === "en" ? title[1] : i18n.language === "de" ? title[2] : title[0]}
        </TaskHeadingTitle>
        {subtitle && subtitleTasks.includes(id) && (
          <TaskHeadingSubtitle>
            {i18n.language === "en" ? subtitle[1] : i18n.language === "de" ? subtitle[2] : subtitle[0]}
          </TaskHeadingSubtitle>
        )}
      </VStack>
    </HStack>
  );
};

const getMultitaskTitle = (task: Task) => {
  const { step, title, name } = task;

  if (
    (task.type === TaskType.ImageSelectMultipleSteps || task.type === TaskType.RadioButtonMultipleSteps) &&
    title &&
    typeof title === "object" &&
    "a" in title
  ) {
    switch (step) {
      case 0:
        return title.a;
      case 1:
        return title.b;
      case 2:
        return title.c;
      case 3:
        return title.d;
      default:
        return name;
    }
  }

  switch (step) {
    case 1:
      return title.a;
    case 2:
      return title.b;
    case 0:
    default:
      return name;
  }
};

interface MultiTaskHeadingProps extends StackProps {
  description: ReactNode;
  headingTitle: MultiTaskTitle;
}

export const MultiTaskHeading = ({ ...rest }: MultiTaskHeadingProps) => {
  const { currentTaskIndex, currentTask } = useFollowUp();
  const { step, id, criteria, subtitle } = currentTask;
  const { t, i18n } = useTranslation();
  const title = getMultitaskTitle(currentTask);

  return (
    <HStack w="full" align="center" {...rest}>
      <Flex justify="center" align="center" h="full">
        <Text
          px="3"
          py="3"
          w="fit-content"
          fontSize="20px"
          color="white"
          bg="#2A0F74"
          borderRadius="100"
          alignItems="center"
        >
          0{currentTaskIndex + 1}
        </Text>
      </Flex>
      <VStack w="100%">
        <HStack w="100%" align="center" justifyContent="space-between">
          <Text textAlign="start" w="full">
            {i18n.language === "en" ? criteria[1] : i18n.language === "de" ? criteria[2] : criteria[0]}
          </Text>
          <VStack align="end" w="100%">
            <HelpdeskComponent />
          </VStack>
        </HStack>
        <TaskHeadingTitle key={`${id}_${title}_${step}`}>
          {i18n.language === "en" ? title[1] : i18n.language === "de" ? title[2] : title[0]}
        </TaskHeadingTitle>
        {subtitle && subtitleTasks.includes(id) && (
          <TaskHeadingSubtitle>
            {i18n.language === "en" ? subtitle[1] : i18n.language === "de" ? subtitle[2] : subtitle[0]}
          </TaskHeadingSubtitle>
        )}
      </VStack>
    </HStack>
  );
};
